import { useRef } from "react";
import "./ResetConfirmModal.css";
import { useModalA11y } from "./useModalA11y";
import { useI18n } from "../i18n";

/**
 * Confirmation for "Start a new conversation" (hazard log HAZ 011, Priya
 * 24 Jul).
 *
 * Reset clears the resident's answers and begins a fresh session, which is
 * destructive and was previously one silent tap on a header icon. The action
 * now lives as a quiet footer link and asks first, so it cannot be triggered
 * by a stray tap next to the buttons a resident actually wants.
 *
 * "Cancel" is the safe default and takes focus, matching the decline pop-up:
 * Escape and a backdrop click both cancel rather than confirm.
 */
export function ResetConfirmModal({
  onCancel,
  onConfirm,
}: {
  /** Close and leave the conversation exactly as it was. */
  onCancel: () => void;
  /** Clear the session and start again. */
  onConfirm: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(dialogRef, onCancel);

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div
        ref={dialogRef}
        className="modal reset-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-modal-title"
      >
        <h2 className="reset-modal__lead" id="reset-modal-title">
          {t("reset.title")}
        </h2>
        <p className="reset-modal__text">
          This clears the answers you've given so far and starts again from the
          beginning. It can't be undone.
        </p>

        <div className="reset-modal__actions">
          <button type="button" className="reset-modal__cancel" data-autofocus onClick={onCancel}>
            {t("reset.cancel")}
          </button>
          <button type="button" className="reset-modal__confirm" onClick={onConfirm}>
            {t("reset.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
